import { ReactNode, useCallback } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';

import { cx } from '@libs/classnames';
import { Loader } from '@ui/components/Loader';
import { Table, TableCell, TableRow } from '@ui/components/Table';
import { Tabs } from '@ui/components/Tabs';

import { isSchoolEvent } from '@features/chessrussian/customizations/utils/event-filters';
import { MemberAux } from '@features/club/typings';
import {
  ParticipantDto,
  RcfAttributes,
  Tournament,
  TournamentTeam,
} from '@features/shared/api/typings';
import { useTournamentStates } from '@features/tournament/hooks/useTournamentStates';
import { DragPreview } from '@features/tournament/ui/components/TournamentParticipantsEdit/components/DragPreview';
import { Footer } from '@features/tournament/ui/components/TournamentParticipantsEdit/components/Footer';
import {
  forcePositionInTeamForParticipant,
  insertParticipantIntoTeamEnd,
} from '@features/tournament/ui/components/TournamentParticipantsEdit/components/participantsHelpers';
import { TableBody } from '@features/tournament/ui/components/TournamentParticipantsEdit/components/TableBody';
import { useOnDragEnd } from '@features/tournament/ui/components/TournamentParticipantsEdit/components/useOnDragEnd';
import { useOnDragOver } from '@features/tournament/ui/components/TournamentParticipantsEdit/components/useOnDragOver';
import { useTabs } from '@features/tournament/ui/components/TournamentParticipantsEdit/components/useTabs';
import { ParticipantsTab } from '@features/tournament/ui/components/TournamentParticipantsEdit/types';

import styles from './TournamentParticipantsEdit.module.css';

export type Props = {
  className?: string;
  tournament: Tournament;
  participants?: ParticipantDto[];
  isPending?: boolean;
  tab: ParticipantsTab;
  onTabChange: (newTab: ParticipantsTab) => void;
  // modal
  showModal: () => void;
  modal?: ReactNode;
  // actions
  onCreateTeam?: (teamId: string) => void;
  onBanTeam?: (teamId: string) => void;
  onRenameTeam?: (teamId: string, name: string) => void;
  onDeleteTeam?: (teamId: string) => void;
  onDeleteParticipant?: (id: string) => void;
  onAcceptParticipant?: (id: string) => void;
  onRestoreParticipant?: (id: string) => void;
  onRemoveParticipant?: (id: string) => void;
  onBanParticipant?: (id: string) => void;
  onParticipantPgnDownload?: (id: string) => void;
  onRcfAttributesChange?: (newValue: RcfAttributes) => void;
  // требование к камерам на видеоконтроле: колонка без заголовка, когда есть обработчик
  onCamerasChange?: (userId: string, aux: MemberAux | null) => void;
  onRearrangeParticipants?: (participants: ParticipantDto[]) => void;
  onRearrangeTeams?: (teams: TournamentTeam[]) => void;
  isRcf: boolean;
};
export function TournamentParticipantsEdit({
  className,
  tournament,
  participants,
  isPending,
  tab,
  onTabChange,
  onRearrangeParticipants,
  onRearrangeTeams,
  showModal,
  modal,
  onCreateTeam,
  onBanTeam,
  onRenameTeam,
  onDeleteTeam,
  onDeleteParticipant,
  onAcceptParticipant,
  onRestoreParticipant,
  onRemoveParticipant,
  onBanParticipant,
  onParticipantPgnDownload,
  onRcfAttributesChange,
  onCamerasChange,
  isRcf,
}: Props) {
  const showTeams =
    (tournament.mode === 'Team' ||
      tournament.mode === 'PrivateTeam' ||
      tournament.mode === 'TwoOnTwo') &&
    tab === 'Participating';
  const { isEditable } = useTournamentStates(tournament);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        delay: 100,
        tolerance: 5,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 300,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const onSelectTeam = useCallback(
    (userId: string, teamId: string | null) => {
      if (!participants || !tournament.teamModeSettings?.size || !onRearrangeParticipants) return;
      onRearrangeParticipants(
        insertParticipantIntoTeamEnd(
          userId,
          teamId,
          participants,
          tournament.teamModeSettings.size,
        ),
      );
    },
    [participants, tournament.teamModeSettings, onRearrangeParticipants],
  );

  const onDragEnd = useOnDragEnd(
    tournament,
    onRearrangeParticipants,
    onRearrangeTeams,
    participants,
  );

  const onDragOver = useOnDragOver(tournament, onRearrangeParticipants, participants);

  const handleSetOrder = useCallback(
    (userId: string, num: number) => {
      if (!onRearrangeParticipants || !tournament.teamModeSettings || !participants) return;
      onRearrangeParticipants(
        forcePositionInTeamForParticipant(
          userId,
          num,
          participants!,
          tournament.teamModeSettings.size,
        ),
      );
    },
    [onRearrangeParticipants, participants, tournament],
  );

  const sortDisabled = !onRearrangeParticipants;
  const showNumbers = tab === 'Participating';

  let nameColSpan = 3;
  if (sortDisabled) --nameColSpan;
  if (!showNumbers) --nameColSpan;

  return (
    <div className={cx(className, styles.root)}>
      <Tabs
        className={styles.tabs}
        tabs={useTabs(tournament, isEditable)}
        onChange={onTabChange}
        value={tab}
      />

      {isPending && <Loader className={styles.loader} centered />}

      {!isPending && (
        <>
          <DndContext sensors={sensors} onDragEnd={onDragEnd} onDragOver={onDragOver}>
            <Table className={styles.table}>
              <TableRow header>
                <TableCell colSpan={nameColSpan}>Имя</TableCell>
                <TableCell>Email</TableCell>
                {isRcf && <TableCell>ФШР&nbsp;ID</TableCell>}
                {onCamerasChange && <TableCell slim leftGap />}
                {isSchoolEvent(tournament) && <TableCell left>Тренер</TableCell>}
                <TableCell right leftGap>
                  Рейтинг
                </TableCell>
                <TableCell right className={styles.buttonColumn}></TableCell>
              </TableRow>

              <TableBody
                tournament={tournament}
                participants={participants}
                showTeams={showTeams}
                showNumbers={showNumbers}
                isRcf={isRcf}
                onBanTeam={onBanTeam}
                sortDisabled={sortDisabled}
                onRenameTeam={onRenameTeam}
                onDeleteTeam={onDeleteTeam}
                onDelete={onDeleteParticipant}
                onAccept={onAcceptParticipant}
                onRestore={onRestoreParticipant}
                onRemove={onRemoveParticipant}
                onBan={onBanParticipant}
                onPgnDownload={onParticipantPgnDownload}
                onSelectTeam={onSelectTeam}
                onRcfAttributesChange={onRcfAttributesChange}
                onCamerasChange={onCamerasChange}
                onSetOrder={
                  isEditable && tournament.teamModeSettings?.numberInTeamMode === 'NoShift'
                    ? handleSetOrder
                    : undefined
                }
              />
            </Table>

            <DragPreview
              participants={participants}
              teams={tournament.teams}
              isRcf={isRcf}
              showCameras={!!onCamerasChange}
              isSchoolEvent={isSchoolEvent(tournament)}
            />
          </DndContext>

          <Footer
            tab={tab}
            tournament={tournament}
            participants={participants}
            onCreateTeam={onCreateTeam}
            showModal={showModal}
          />

          {modal}
        </>
      )}
    </div>
  );
}
